import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { media, textSize } from '../../../styles/brand';

import api, { getApiErrorMessage } from '../../../services/api';
import { useToast } from '../../../hooks/toast';
import PrimaryButton from '../../../components/Buttons/PrimaryButton';
import SecondaryButton from '../../../components/Buttons/SecondaryButton';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  ScheduleIcon,
  ArrowBackIcon,
} from '../../../assets/svg/SVGComponents';
import SaveIcon from '../../../assets/svg/SVGComponents/SaveIcon';
import { CrossIcon } from '../../../assets/svg/SVGComponents';
import {
  CATEGORY_NUMBER_TO_LABEL,
  MODALITY_SLUG_TO_NAME,
} from '../CreateTournament';
import {
  formatWallClockTime as formatTime,
  buildWallClockIso as buildWallClock,
} from '../../../utils/scheduleDate';

import {
  ArenaSelect,
  ArenaTagsRow,
  BreakCell,
  BreakRow,
  CardFooter,
  CardTitle,
  ConfigCard,
  ConfigGrid,
  ConfigLeftCol,
  ConfigRightCol,
  DurationInput,
  FieldGroup,
  FieldLabel,
  InfoSection,
  InfoTag,
  InfoTagsRow,
  InlineFieldGroup,
  IntervalDivider,
  IntervalRow,
  ModalitiesConfigSection,
  ModalityConfigBlock,
  ModalityConfigDivider,
  ModalityConfigTitle,
  ModalityGroupCell,
  ModalityGroupRow,
  OrdemBtn,
  OrdemCell,
  OrdemNumber,
  PageHeader,
  PageTitle,
  PageWrapper,
  ScheduleTable,
  EditSlotCard,
  EditSlotFields,
  EditSlotHeader,
  EmptyNote,
  MobileBreak,
  MobileGroupTitle,
  MobileSchedule,
  OrderButton,
  SaveHint,
  OrderControls,
  SkeletonBlock,
  StyledInput,
  StyledSelect,
  TableHead,
  TableRow,
  TableScroller,
  TableWrapper,
  Td,
  TdTime,
  Th,
} from './styles';
import ModalityRulesFields from '../../../components/ModalityRulesFields';
import { TimeInput } from '../../../components/Tournament';
import {
  hasErrors,
  regulationRules,
  TModalityRulesDraft,
  TModalityRulesErrors,
  validateModalityRules,
} from '../../../utils/tournament/modalityRules';

/* ── Interfaces ── */

interface IInterval {
  id: number;
  name: string;
  start: string;
  end: string;
}

interface IModalityDuration {
  roundDurationMin: string;
  roundDurationSec: string;
  prepDurationMin: string;
  prepDurationSec: string;
  bonusTimeMin: string;
  bonusTimeSec: string;
  bonusPoints: string;
}

const durationSeconds = (min: string, sec: string): number | null =>
  min === '' && sec === '' ? null : Number(min || 0) * 60 + Number(sec || 0);

/** Duração/bônus do cronograma no formato do campo compartilhado. */
const toRulesDraft = (dur: IModalityDuration): TModalityRulesDraft => ({
  time_limit_seconds: durationSeconds(
    dur.roundDurationMin,
    dur.roundDurationSec,
  ),
  max_rounds: null,
  bonus_time_seconds: durationSeconds(dur.bonusTimeMin, dur.bonusTimeSec),
  bonus_points: dur.bonusPoints === '' ? null : Number(dur.bonusPoints),
});

const splitSeconds = (seconds: number | null) => ({
  min: seconds === null ? '' : String(Math.floor(seconds / 60)),
  sec: seconds === null ? '' : String(seconds % 60),
});

const durationFromSlug = (slug: string): IModalityDuration => {
  const rules = regulationRules(slug);
  return {
    roundDurationMin: String(Math.floor(rules.time_limit_seconds / 60)),
    roundDurationSec: String(rules.time_limit_seconds % 60),
    prepDurationMin: '0',
    prepDurationSec: '0',
    bonusTimeMin: String(Math.floor(rules.bonus_time_seconds / 60)),
    bonusTimeSec: String(rules.bonus_time_seconds % 60),
    bonusPoints: String(rules.bonus_points),
  };
};

interface IArenaOption {
  id: string;
  name: string;
  modality: string;
}

interface IJudgeOption {
  id: string;
  name: string;
}

interface IRoundRow {
  id: string;
  teamId: string | null;
  team: string;
  arenaId: string;
  arenaName: string;
  judgeId: string | null;
  judgeName: string;
  starts_at: string;
  ends_at: string;
  modality: string;
  modalitySlug: string;
  category: string;
  isBreak: boolean;
  breakLabel: string;
  animDir: 'up' | 'down' | null;
}

interface IApiSlot {
  id: string;
  round_number: number;
  is_break: boolean;
  break_label: string | null;
  starts_at: string;
  ends_at: string;
  status: string;
  arena: {
    id: string;
    name: string;
    tournament_modality_id: string;
  } | null;
  team: {
    id: string;
    name: string;
    category: string;
    tournament_modality_id: string;
  } | null;
  judge: { id: string; name: string } | null;
}

interface IApiModality {
  id: string;
  modality: string;
  time_limit_seconds: number;
  time_periods_seconds: number;
  bonus_time_seconds: number;
  bonus_points: number;
  arenas: Array<{
    id: string;
    name: string;
    tournament_modality_id: string;
    is_active: boolean;
  }>;
}

interface IApiTournament {
  id: string;
  name: string;
  date?: string | null;
  end_date?: string | null;
  modalities?: IApiModality[];
}

type TScheduleReadjustReason = 'no-schedule' | 'in-progress' | 'no-window';

/* Diff devolvido pelas edições estruturais (ex.: PATCH /modalities/:id) que
   reajustam o cronograma automaticamente na mesma transação. */
interface IScheduleDiff {
  readjusted: boolean;
  reason?: TScheduleReadjustReason;
  slots: IApiSlot[];
  timeHint?: { sufficient: boolean; differenceMinutes: number };
}

interface IEditScheduleResult<T> {
  data: T;
  schedule: IScheduleDiff;
}

interface IJudgeRecord {
  id: string;
  user_id: string;
  user: { id: string; name: string };
}

/* ── Helpers ── */

const TIME_OPTIONS = Array.from({ length: 24 * 12 }, (_, i) => {
  const totalMins = i * 5;
  const h = String(Math.floor(totalMins / 60)).padStart(2, '0');
  const m = String(totalMins % 60).padStart(2, '0');
  return `${h}:${m}`;
});

const extractTimeStr = (isoStr?: string | null): string => {
  if (!isoStr) return '09:00';
  return formatTime(isoStr);
};

const buildIso = (baseDateIso: string, timeStr: string): string => {
  try {
    const [h, m] = timeStr.split(':').map(Number);
    const d = new Date(baseDateIso);
    d.setUTCHours(h, m, 0, 0);
    return d.toISOString();
  } catch {
    return baseDateIso;
  }
};

const shiftIsoByMinutes = (isoStr: string, diffMinutes: number): string => {
  try {
    const d = new Date(isoStr);
    d.setUTCMinutes(d.getUTCMinutes() + diffMinutes);
    return d.toISOString();
  } catch {
    return isoStr;
  }
};

const slotToRow = (
  slot: IApiSlot,
  modalityById: Map<string, string>,
): IRoundRow => {
  const modalityId =
    slot.arena?.tournament_modality_id ??
    slot.team?.tournament_modality_id ??
    '';
  const modalitySlug = modalityById.get(modalityId) ?? '';
  const modalityLabel =
    MODALITY_SLUG_TO_NAME[modalitySlug] ?? modalitySlug ?? '-';
  const categoryNum = parseInt(
    slot.team?.category?.replace('category_', '') ?? '',
    10,
  );
  const categoryLabel =
    CATEGORY_NUMBER_TO_LABEL[categoryNum] ?? slot.team?.category ?? '-';

  return {
    id: slot.id,
    teamId: slot.team?.id ?? null,
    team: slot.team?.name ?? '-',
    arenaId: slot.arena?.id ?? '',
    arenaName: slot.arena?.name ?? '-',
    judgeId: slot.judge?.id ?? null,
    judgeName: slot.judge?.name ?? '-',
    starts_at: slot.starts_at,
    ends_at: slot.ends_at,
    modality: modalityLabel,
    modalitySlug,
    category: categoryLabel,
    isBreak: slot.is_break,
    breakLabel: slot.break_label || 'Intervalo',
    animDir: null,
  };
};

interface ISlotSnapshot {
  starts_at: string;
  ends_at: string;
  arenaId: string;
  judgeId: string | null;
  teamId: string | null;
}

const buildSlotSnapshot = (rows: IRoundRow[]): Map<string, ISlotSnapshot> => {
  const snapshot = new Map<string, ISlotSnapshot>();
  rows
    .filter(r => !r.isBreak)
    .forEach(r =>
      snapshot.set(r.id, {
        starts_at: r.starts_at,
        ends_at: r.ends_at,
        arenaId: r.arenaId,
        judgeId: r.judgeId,
        teamId: r.teamId,
      }),
    );
  return snapshot;
};

/* ── Component ── */

const TournamentTimeline: React.FC = () => {
  const isMobile = useMediaQuery({ query: media.md });
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [tournamentName, setTournamentName] = useState('');
  const [tournamentDate, setTournamentDate] = useState<string>(
    new Date().toISOString(),
  );
  const [startTime, setStartTime] = useState('09:00');
  const [modalityDurations, setModalityDurations] = useState<
    Record<string, IModalityDuration>
  >({});
  const [modalities, setModalities] = useState<IApiModality[]>([]);

  const [arenaOptions, setArenaOptions] = useState<IArenaOption[]>([]);
  const [judgeOptions, setJudgeOptions] = useState<IJudgeOption[]>([]);

  const [intervalCount, setIntervalCount] = useState(0);
  const [intervals, setIntervals] = useState<IInterval[]>([]);

  const [rounds, setRounds] = useState<IRoundRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const modalityByIdRef = useRef<Map<string, string>>(new Map());
  const initialSlotsRef = useRef<Map<string, ISlotSnapshot>>(new Map());
  const animTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  useEffect(() => {
    const timers = animTimers.current;
    return () => {
      Object.values(timers).forEach(clearTimeout);
    };
  }, []);

  useEffect(() => {
    if (!id) return;

    Promise.all([
      api.get<{ tournament: IApiTournament; slots: IApiSlot[] }>(
        `/tournaments/${id}/schedule`,
      ),
      api.get<IJudgeRecord[]>(`/tournaments/${id}/judges`),
    ])
      .then(([scheduleRes, judgesRes]) => {
        const { tournament, slots } = scheduleRes.data;
        const judgeRecords = judgesRes.data ?? [];

        setTournamentName(tournament.name);

        if (tournament.date) {
          setTournamentDate(tournament.date);
        }

        const durationsMap: Record<string, IModalityDuration> = {};
        tournament.modalities?.forEach(m => {
          durationsMap[m.id] = {
            roundDurationMin: String(Math.floor(m.time_limit_seconds / 60)),
            roundDurationSec: String(m.time_limit_seconds % 60),
            prepDurationMin: String(Math.floor(m.time_periods_seconds / 60)),
            prepDurationSec: String(m.time_periods_seconds % 60),
            bonusTimeMin: String(Math.floor((m.bonus_time_seconds ?? 0) / 60)),
            bonusTimeSec: String((m.bonus_time_seconds ?? 0) % 60),
            bonusPoints: String(m.bonus_points ?? 0),
          };
        });
        setModalityDurations(durationsMap);
        setModalities(tournament.modalities ?? []);

        /* Build lookup: tournament_modality_id → modality slug */
        const modalityById = new Map<string, string>();
        const arenaMap = new Map<string, IArenaOption>();
        tournament.modalities?.forEach(m => {
          modalityById.set(m.id, m.modality);
          m.arenas?.forEach(a => {
            arenaMap.set(a.id, {
              id: a.id,
              name: a.name,
              modality: m.modality,
            });
          });
        });
        modalityByIdRef.current = modalityById;

        setArenaOptions(Array.from(arenaMap.values()));
        setJudgeOptions(
          judgeRecords.map(j => ({ id: j.user_id, name: j.user.name })),
        );

        const sorted = [...slots].sort(
          (a, b) =>
            new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime(),
        );

        if (sorted.length > 0) {
          setStartTime(extractTimeStr(sorted[0].starts_at));
        }

        /* Populate break editor from existing break slots */
        const breakSlots = sorted.filter(s => s.is_break);
        if (breakSlots.length > 0) {
          setIntervalCount(breakSlots.length);
          setIntervals(
            breakSlots.map((slot, i) => ({
              id: i + 1,
              name: slot.break_label || 'Intervalo',
              start: extractTimeStr(slot.starts_at),
              end: extractTimeStr(slot.ends_at),
            })),
          );
        }

        const initialRows = sorted.map(slot => slotToRow(slot, modalityById));
        setRounds(initialRows);
        initialSlotsRef.current = buildSlotSnapshot(initialRows);
        setIsDirty(false);
      })
      .catch(() => {
        addToast({
          type: 'error',
          expression: 'Erro!',
          description: 'Não foi possível carregar o cronograma.',
        });
      })
      .finally(() => setIsLoading(false));
  }, [addToast, id]);

  const triggerAnim = useCallback((rowId: string, dir: 'up' | 'down') => {
    setRounds(prev =>
      prev.map(r => (r.id === rowId ? { ...r, animDir: dir } : r)),
    );
    if (animTimers.current[rowId]) clearTimeout(animTimers.current[rowId]);
    animTimers.current[rowId] = setTimeout(() => {
      setRounds(prev =>
        prev.map(r => (r.id === rowId ? { ...r, animDir: null } : r)),
      );
    }, 320);
  }, []);

  const handleStartTimeChange = (newStartTime: string) => {
    if (!newStartTime) return;

    const referenceTime =
      rounds.length > 0 ? extractTimeStr(rounds[0].starts_at) : startTime;
    const oldDate = new Date(buildIso(tournamentDate, referenceTime));
    const newDate = new Date(buildIso(tournamentDate, newStartTime));
    const diffMinutes = Math.round(
      (newDate.getTime() - oldDate.getTime()) / 60000,
    );

    setStartTime(newStartTime);

    if (diffMinutes === 0) return;

    setRounds(prev =>
      prev.map(r => ({
        ...r,
        starts_at: shiftIsoByMinutes(r.starts_at, diffMinutes),
        ends_at: shiftIsoByMinutes(r.ends_at, diffMinutes),
      })),
    );

    setIntervals(prev =>
      prev.map(iv => ({
        ...iv,
        start: extractTimeStr(
          shiftIsoByMinutes(buildIso(tournamentDate, iv.start), diffMinutes),
        ),
        end: extractTimeStr(
          shiftIsoByMinutes(buildIso(tournamentDate, iv.end), diffMinutes),
        ),
      })),
    );

    setIsDirty(true);
  };

  /*
   * Swap the team between two slots of the SAME modality. Arenas, judges and
   * times stay in place — only which team occupies the slot changes. Limiting
   * the swap to one modality keeps each slot's arena/duration valid (slots of
   * different modalities have different arenas and round durations).
   */
  const moveRow = useCallback(
    (groupTeams: IRoundRow[], index: number, direction: 'up' | 'down') => {
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= groupTeams.length) return;

      const current = groupTeams[index];
      const target = groupTeams[targetIndex];

      setRounds(prev =>
        prev.map(r => {
          if (r.id === current.id) {
            return {
              ...r,
              teamId: target.teamId,
              team: target.team,
              category: target.category,
              animDir: null,
            };
          }
          if (r.id === target.id) {
            return {
              ...r,
              teamId: current.teamId,
              team: current.team,
              category: current.category,
              animDir: null,
            };
          }
          return r;
        }),
      );

      setIsDirty(true);
      triggerAnim(target.id, direction === 'up' ? 'down' : 'up');
      triggerAnim(current.id, direction === 'up' ? 'up' : 'down');
    },
    [triggerAnim],
  );

  const handleArenaChange = useCallback(
    (rowId: string, arenaId: string) => {
      const arena = arenaOptions.find(a => a.id === arenaId);
      if (!arena) return;

      setRounds(prev =>
        prev.map(r =>
          r.id === rowId
            ? { ...r, arenaId: arena.id, arenaName: arena.name }
            : r,
        ),
      );
      setIsDirty(true);
    },
    [arenaOptions],
  );

  const handleJudgeChange = useCallback(
    (rowId: string, judgeId: string) => {
      const judge = judgeOptions.find(j => j.id === judgeId) ?? null;

      setRounds(prev =>
        prev.map(r =>
          r.id === rowId
            ? { ...r, judgeId: judgeId || null, judgeName: judge?.name ?? '-' }
            : r,
        ),
      );
      setIsDirty(true);
    },
    [judgeOptions],
  );

  const updateDuration = (
    modalityId: string,
    field: keyof IModalityDuration,
    value: string,
  ) => {
    setModalityDurations(prev => ({
      ...prev,
      [modalityId]: { ...prev[modalityId], [field]: value },
    }));
    setIsDirty(true);
  };

  const updateRules = (modalityId: string, next: TModalityRulesDraft) => {
    const round = splitSeconds(next.time_limit_seconds);
    const bonus = splitSeconds(next.bonus_time_seconds);
    setModalityDurations(prev => ({
      ...prev,
      [modalityId]: {
        ...prev[modalityId],
        roundDurationMin: round.min,
        roundDurationSec: round.sec,
        bonusTimeMin: bonus.min,
        bonusTimeSec: bonus.sec,
        bonusPoints:
          next.bonus_points === null ? '' : String(next.bonus_points),
      },
    }));
    setIsDirty(true);
  };

  // Mesmas regras da criação: bônus cabe na rodada, pontos entre 0 e 100.
  const rulesErrors = useMemo(() => {
    const errors: Record<string, TModalityRulesErrors> = {};
    Object.entries(modalityDurations).forEach(([modalityId, dur]) => {
      const modalityErrors = validateModalityRules(toRulesDraft(dur), {
        requireRounds: false,
      });
      if (hasErrors(modalityErrors)) errors[modalityId] = modalityErrors;
    });
    return errors;
  }, [modalityDurations]);
  const hasRulesErrors = Object.keys(rulesErrors).length > 0;

  const handleIntervalCountChange = (count: number) => {
    setIsDirty(true);
    setIntervalCount(count);
    setIntervals(prev => {
      if (count > prev.length) {
        const added: IInterval[] = Array.from(
          { length: count - prev.length },
          (_, i) => ({
            id: prev.length + i + 1,
            name: 'Intervalo',
            start: '12:00',
            end: '13:00',
          }),
        );
        return [...prev, ...added];
      }
      return prev.slice(0, count);
    });
  };

  const updateInterval = (
    ivId: number,
    field: keyof IInterval,
    value: string,
  ) => {
    setIntervals(prev =>
      prev.map(iv => (iv.id === ivId ? { ...iv, [field]: value } : iv)),
    );
    setIsDirty(true);
  };

  const handleSave = async () => {
    if (!id || hasRulesErrors) return;
    setIsSaving(true);

    /* Only PATCH slots whose fields actually changed since the last load/save */
    const slotPatches: Promise<unknown>[] = [];
    rounds
      .filter(r => !r.isBreak)
      .forEach(r => {
        const initial = initialSlotsRef.current.get(r.id);
        if (!initial) return;

        const body: Record<string, string | null> = {};
        if (r.starts_at !== initial.starts_at) body.starts_at = r.starts_at;
        if (r.ends_at !== initial.ends_at) body.ends_at = r.ends_at;
        if (r.arenaId !== initial.arenaId) body.arena_id = r.arenaId || null;
        if ((r.judgeId ?? null) !== initial.judgeId)
          body.judge_id = r.judgeId ?? null;
        if ((r.teamId ?? null) !== initial.teamId)
          body.team_id = r.teamId ?? null;

        if (Object.keys(body).length === 0) return;
        slotPatches.push(
          api.patch(`/tournaments/${id}/schedule/slots/${r.id}`, body),
        );
      });

    try {
      /*
       * Ordem importa. Editar slot a slot (arena/juiz/equipe/horário) é ajuste
       * fino e não reajusta. Editar a duração de uma modalidade reajusta o
       * cronograma na mesma transação e devolve o cronograma final em
       * `schedule`. Então: 1) ajustes manuais, 2) intervalos (PUT bulk),
       * 3) durações — assim o `schedule.slots` do reajuste já reflete os
       * intervalos e ajustes recém-salvos e vira a fonte de verdade da tela.
       */
      await Promise.all(slotPatches);

      const breaksResponse = await api.put<IApiSlot[]>(
        `/tournaments/${id}/schedule/breaks`,
        {
          breaks: intervals.map(iv => ({
            starts_at: buildWallClock(tournamentDate, iv.start),
            ends_at: buildWallClock(tournamentDate, iv.end),
            break_label: iv.name || 'Intervalo',
          })),
        },
      );

      // Em SEQUÊNCIA, não em paralelo: cada PATCH reconstrói o cronograma, e
      // duas reconstruções ao mesmo tempo deixavam todos os slots em dobro.
      const modalityResults: { data: IEditScheduleResult<IApiModality> }[] = [];
      for (const [modalityId, dur] of Object.entries(modalityDurations)) {
        // eslint-disable-next-line no-await-in-loop
        const result = await api.patch<IEditScheduleResult<IApiModality>>(
          `/tournaments/${id}/modalities/${modalityId}`,
          {
            time_limit_seconds:
              Number(dur.roundDurationMin) * 60 + Number(dur.roundDurationSec),
            time_periods_seconds:
              Number(dur.prepDurationMin) * 60 + Number(dur.prepDurationSec),
            bonus_time_seconds:
              Number(dur.bonusTimeMin) * 60 + Number(dur.bonusTimeSec),
            bonus_points: Number(dur.bonusPoints),
          },
        );
        modalityResults.push(result);
      }

      /* Último reajuste efetivo é o cronograma completo e mais recente. */
      const readjusted = modalityResults
        .map(res => res.data.schedule)
        .reverse()
        .find(diff => diff?.readjusted);

      let nextRows: IRoundRow[];
      if (readjusted) {
        nextRows = readjusted.slots
          .map(slot => slotToRow(slot, modalityByIdRef.current))
          .sort(
            (a, b) =>
              new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime(),
          );
      } else {
        const newBreakRows = breaksResponse.data.map(slot =>
          slotToRow(slot, modalityByIdRef.current),
        );
        nextRows = [...rounds.filter(r => !r.isBreak), ...newBreakRows].sort(
          (a, b) =>
            new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime(),
        );
      }

      setRounds(nextRows);
      initialSlotsRef.current = buildSlotSnapshot(nextRows);
      setIsDirty(false);

      /* Avisos quando o backend NÃO reajustou (entidade salva mesmo assim). */
      const notReadjusted = modalityResults
        .map(res => res.data.schedule)
        .find(diff => diff && !diff.readjusted);
      if (notReadjusted?.reason === 'in-progress') {
        addToast({
          type: 'info',
          expression: 'Atenção',
          description:
            'Tempos salvos, mas o cronograma em andamento não foi reajustado.',
        });
      } else if (notReadjusted?.reason === 'no-window') {
        addToast({
          type: 'info',
          expression: 'Atenção',
          description:
            'Defina as datas do torneio para reajustar o cronograma automaticamente.',
        });
      } else if (readjusted?.timeHint && !readjusted.timeHint.sufficient) {
        addToast({
          type: 'info',
          expression: 'Atenção',
          description:
            'O cronograma ultrapassa a janela de tempo prevista do torneio.',
        });
      }

      addToast({
        type: 'success',
        expression: 'Sucesso!',
        description: 'Cronograma salvo com sucesso.',
      });
    } catch (err) {
      // A mensagem do servidor diz o motivo (ex.: conflito de horário).
      addToast({
        type: 'error',
        expression: 'Não foi possível salvar o cronograma',
        description: getApiErrorMessage(err) ?? 'Tente novamente em instantes.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (
      isDirty &&
      !window.confirm(
        'Há alterações não salvas. Deseja sair sem salvar o cronograma?',
      )
    ) {
      return;
    }
    navigate(`/tournament/${id}`);
  };

  const actualEndTime = useMemo(() => {
    if (rounds.length === 0) return '18:00';
    let maxDate = new Date(rounds[0].ends_at).getTime();
    for (const r of rounds) {
      const d = new Date(r.ends_at).getTime();
      if (d > maxDate) maxDate = d;
    }
    return extractTimeStr(new Date(maxDate).toISOString());
  }, [rounds]);

  /*
   * Group team slots by modality (preserving first-appearance order). Break
   * slots are global, so they are interleaved by time inside each group. Each
   * group exposes its ordered team rows so the reorder arrows only ever swap
   * teams within the same modality.
   */
  const modalityGroups = useMemo(() => {
    const breakRows = rounds.filter(r => r.isBreak);
    const order: string[] = [];
    const teamsBySlug = new Map<string, IRoundRow[]>();

    rounds
      .filter(r => !r.isBreak)
      .forEach(r => {
        const key = r.modalitySlug || '__none__';
        if (!teamsBySlug.has(key)) {
          teamsBySlug.set(key, []);
          order.push(key);
        }
        teamsBySlug.get(key)?.push(r);
      });

    return order.map(slug => {
      const teams = teamsBySlug.get(slug) ?? [];
      const teamIndexById = new Map<string, number>();
      teams.forEach((t, i) => teamIndexById.set(t.id, i));
      const display = [...teams, ...breakRows].sort(
        (a, b) =>
          new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime(),
      );
      return {
        slug,
        label: MODALITY_SLUG_TO_NAME[slug] ?? teams[0]?.modality ?? '—',
        teams,
        teamIndexById,
        display,
      };
    });
  }, [rounds]);

  return (
    <PageWrapper>
      {/* Page header */}
      <PageHeader>
        <Link to={`/tournament/${id}`}>
          <ArrowBackIcon
            width="1.33rem"
            stroke="#1E88E5"
            strokeWidth={3}
            fill="none"
          />
          Voltar
        </Link>
        <div>
          <ScheduleIcon
            width="2rem"
            stroke="#FF9800"
            strokeWidth={3}
            fill="none"
          />
          <PageTitle>Cronograma</PageTitle>
        </div>
      </PageHeader>

      {/* Config card */}
      <ConfigCard>
        <CardTitle>
          {isLoading ? (
            <SkeletonBlock $w="16rem" $h="1.4rem" />
          ) : (
            tournamentName || '—'
          )}
        </CardTitle>

        <ConfigGrid>
          {/* LEFT column */}
          <ConfigLeftCol>
            <InlineFieldGroup>
              <FieldGroup>
                <FieldLabel>Início do torneio</FieldLabel>
                <TimeInput
                  inputComponent={StyledInput}
                  aria-label="Início do torneio (HH:MM, 24h)"
                  value={startTime}
                  onChange={handleStartTimeChange}
                  disabled={isLoading || isSaving}
                  style={{
                    minWidth: '7rem',
                    fontFamily: 'RedHatDisplay-Regular',
                  }}
                />
              </FieldGroup>

              <FieldGroup>
                <FieldLabel>Tempo de finalização previsto</FieldLabel>
                <StyledInput
                  type="text"
                  aria-label="Tempo de finalização previsto"
                  value={actualEndTime}
                  readOnly
                  disabled
                  style={{
                    minWidth: '7rem',
                    fontFamily: 'RedHatDisplay-Regular',
                    backgroundColor: '#f5f5f5',
                    color: '#888',
                    cursor: 'not-allowed',
                  }}
                />
              </FieldGroup>
            </InlineFieldGroup>

            <InfoSection>
              <FieldLabel>Juízes Cadastrados:</FieldLabel>
              <InfoTagsRow>
                {judgeOptions.length === 0 && (
                  <span style={{ fontSize: textSize(0.8), color: '#888' }}>
                    Nenhum juiz cadastrado
                  </span>
                )}
                {judgeOptions.map(j => (
                  <InfoTag key={j.id}>{j.name}</InfoTag>
                ))}
              </InfoTagsRow>
            </InfoSection>

            {modalities.length > 0 && (
              <ModalitiesConfigSection>
                {modalities.map((mod, idx) => {
                  const modalityName =
                    MODALITY_SLUG_TO_NAME[mod.modality] ?? mod.modality;
                  const modArenas = arenaOptions.filter(
                    a => a.modality === mod.modality,
                  );
                  const dur =
                    modalityDurations[mod.id] ?? durationFromSlug(mod.modality);

                  return (
                    <React.Fragment key={mod.id}>
                      {idx > 0 && <ModalityConfigDivider />}
                      <ModalityConfigBlock>
                        <ModalityConfigTitle>
                          {modalityName}
                        </ModalityConfigTitle>

                        <ModalityRulesFields
                          modality={mod.modality}
                          value={toRulesDraft(dur)}
                          errors={rulesErrors[mod.id]}
                          disabled={isLoading}
                          showRounds={false}
                          onChange={next => updateRules(mod.id, next)}
                        />

                        <InlineFieldGroup>
                          <FieldGroup>
                            <FieldLabel>Tempo de Preparação</FieldLabel>
                            <InlineFieldGroup style={{ gap: '0.4rem' }}>
                              <DurationInput
                                type="number"
                                min={0}
                                max={60}
                                value={dur.prepDurationMin}
                                onChange={e =>
                                  updateDuration(
                                    mod.id,
                                    'prepDurationMin',
                                    e.target.value,
                                  )
                                }
                                disabled={isLoading}
                              />
                              <span
                                style={{
                                  fontSize: textSize(0.9),
                                  color: '#555',
                                  fontFamily: 'RedHatDisplay-Regular',
                                  paddingBottom: '0.6rem',
                                }}
                              >
                                min
                              </span>
                              <DurationInput
                                type="number"
                                min={0}
                                max={59}
                                value={dur.prepDurationSec}
                                onChange={e =>
                                  updateDuration(
                                    mod.id,
                                    'prepDurationSec',
                                    e.target.value,
                                  )
                                }
                                disabled={isLoading}
                              />
                              <span
                                style={{
                                  fontSize: textSize(0.9),
                                  color: '#555',
                                  fontFamily: 'RedHatDisplay-Regular',
                                  paddingBottom: '0.6rem',
                                }}
                              >
                                s
                              </span>
                            </InlineFieldGroup>
                          </FieldGroup>
                        </InlineFieldGroup>

                        <ArenaTagsRow>
                          {modArenas.length === 0 ? (
                            <span
                              style={{ fontSize: textSize(0.8), color: '#888' }}
                            >
                              Nenhuma arena cadastrada
                            </span>
                          ) : (
                            modArenas.map(a => (
                              <InfoTag key={a.id}>{a.name}</InfoTag>
                            ))
                          )}
                        </ArenaTagsRow>
                      </ModalityConfigBlock>
                    </React.Fragment>
                  );
                })}
              </ModalitiesConfigSection>
            )}
          </ConfigLeftCol>

          {/* RIGHT column */}
          <ConfigRightCol>
            <FieldGroup>
              <FieldLabel>Números de Intervalos</FieldLabel>
              <StyledSelect
                value={intervalCount}
                onChange={e =>
                  handleIntervalCountChange(Number(e.target.value))
                }
                style={{ maxWidth: '5rem' }}
              >
                {[0, 1, 2, 3, 4, 5].map(n => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </StyledSelect>
            </FieldGroup>

            {intervals.map((iv, idx) => (
              <React.Fragment key={iv.id}>
                {idx > 0 && <IntervalDivider />}
                <IntervalRow>
                  <FieldGroup>
                    <FieldLabel>Nome do Intervalo</FieldLabel>
                    <StyledInput
                      placeholder="Ex: Almoço"
                      value={iv.name}
                      onChange={e =>
                        updateInterval(iv.id, 'name', e.target.value)
                      }
                    />
                  </FieldGroup>

                  <FieldGroup>
                    <FieldLabel>Início do Intervalo</FieldLabel>
                    <StyledSelect
                      value={iv.start}
                      onChange={e =>
                        updateInterval(iv.id, 'start', e.target.value)
                      }
                    >
                      {TIME_OPTIONS.map(h => (
                        <option key={h} value={h}>
                          {h}
                        </option>
                      ))}
                    </StyledSelect>
                  </FieldGroup>

                  <FieldGroup>
                    <FieldLabel>Fim do Intervalo</FieldLabel>
                    <StyledSelect
                      value={iv.end}
                      onChange={e =>
                        updateInterval(iv.id, 'end', e.target.value)
                      }
                    >
                      {TIME_OPTIONS.map(h => (
                        <option key={h} value={h}>
                          {h}
                        </option>
                      ))}
                    </StyledSelect>
                  </FieldGroup>
                </IntervalRow>
              </React.Fragment>
            ))}
          </ConfigRightCol>
        </ConfigGrid>

        <CardFooter>
          {/* Botão desabilitado sem motivo parece quebrado: diz o porquê. */}
          <SaveHint aria-live="polite">
            {!isSaving &&
              (hasRulesErrors
                ? 'Corrija os campos destacados para salvar'
                : !isDirty
                  ? 'Nenhuma alteração para salvar'
                  : '')}
          </SaveHint>
          <SecondaryButton type="button" onClick={handleCancel}>
            <CrossIcon
              width="0.9rem"
              height="0.9rem"
              stroke="#323232"
              strokeWidth={1.5}
            />
            Cancelar
          </SecondaryButton>
          <PrimaryButton
            type="button"
            onClick={handleSave}
            disabled={isSaving || !isDirty || hasRulesErrors}
            loading={isSaving}
          >
            <SaveIcon
              width="1.4rem"
              fill="none"
              stroke="#fff"
              strokeWidth={2.5}
            />
            {isSaving ? 'Salvando...' : 'Salvar cronograma'}
          </PrimaryButton>
        </CardFooter>
      </ConfigCard>

      {/* Cronograma: cards no celular, tabela no desktop */}
      {isMobile ? (
        <MobileSchedule>
          {isLoading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <EditSlotCard key={i}>
                <SkeletonBlock $h="0.9rem" $w="60%" />
                <SkeletonBlock $h="2.75rem" />
              </EditSlotCard>
            ))
          ) : rounds.length === 0 ? (
            <EmptyNote>Nenhuma rodada cadastrada ainda.</EmptyNote>
          ) : (
            modalityGroups.map(group => (
              <React.Fragment key={group.slug}>
                <MobileGroupTitle>{group.label}</MobileGroupTitle>

                {group.display.map(row => {
                  if (row.isBreak) {
                    return (
                      <MobileBreak key={`${group.slug}-${row.id}`}>
                        {row.breakLabel} · {formatTime(row.starts_at)} –{' '}
                        {formatTime(row.ends_at)}
                      </MobileBreak>
                    );
                  }

                  const teamIndex = group.teamIndexById.get(row.id) ?? 0;

                  return (
                    <EditSlotCard key={row.id}>
                      <EditSlotHeader>
                        <div>
                          <strong>{row.team}</strong>
                          <span>
                            {formatTime(row.starts_at)} · {row.category}
                          </span>
                        </div>
                        <OrderControls>
                          <OrderButton
                            type="button"
                            aria-label={`Mover ${row.team} para cima`}
                            disabled={teamIndex === 0}
                            onClick={() =>
                              moveRow(group.teams, teamIndex, 'up')
                            }
                          >
                            <ChevronUpIcon
                              width="0.8rem"
                              stroke="currentColor"
                              fill="none"
                              strokeWidth={2.5}
                            />
                          </OrderButton>
                          <OrdemNumber aria-label="Ordem">
                            {teamIndex + 1}
                          </OrdemNumber>
                          <OrderButton
                            type="button"
                            aria-label={`Mover ${row.team} para baixo`}
                            disabled={teamIndex >= group.teams.length - 1}
                            onClick={() =>
                              moveRow(group.teams, teamIndex, 'down')
                            }
                          >
                            <ChevronDownIcon
                              width="0.8rem"
                              stroke="currentColor"
                              fill="none"
                              strokeWidth={2.5}
                            />
                          </OrderButton>
                        </OrderControls>
                      </EditSlotHeader>
                      <EditSlotFields>
                        <label>
                          Arena
                          <ArenaSelect
                            value={row.arenaId}
                            onChange={e =>
                              handleArenaChange(row.id, e.target.value)
                            }
                          >
                            <option value="">Selecione...</option>
                            {arenaOptions
                              .filter(
                                a =>
                                  !row.modalitySlug ||
                                  a.modality === row.modalitySlug,
                              )
                              .map(a => (
                                <option key={a.id} value={a.id}>
                                  {a.name}
                                </option>
                              ))}
                          </ArenaSelect>
                        </label>
                        <label>
                          Juiz
                          <ArenaSelect
                            value={row.judgeId ?? ''}
                            onChange={e =>
                              handleJudgeChange(row.id, e.target.value)
                            }
                          >
                            <option value="">Sem juiz</option>
                            {judgeOptions.map(j => (
                              <option key={j.id} value={j.id}>
                                {j.name}
                              </option>
                            ))}
                          </ArenaSelect>
                        </label>
                      </EditSlotFields>
                    </EditSlotCard>
                  );
                })}
              </React.Fragment>
            ))
          )}
        </MobileSchedule>
      ) : (
        <TableWrapper>
          <TableScroller>
            <ScheduleTable>
              <colgroup>
                <col style={{ width: '22%' }} />
                <col style={{ width: '13%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '19%' }} />
                <col style={{ width: '16%' }} />
                <col style={{ width: '8%' }} />
                <col style={{ width: '8%' }} />
              </colgroup>
              <TableHead>
                <tr>
                  <Th>Equipe</Th>
                  <Th>Arena</Th>
                  <Th>Modalidade</Th>
                  <Th>Categoria</Th>
                  <Th>Juiz</Th>
                  <Th style={{ textAlign: 'center' }}>Horário</Th>
                  <Th style={{ textAlign: 'center' }}>Ordem</Th>
                </tr>
              </TableHead>
              <tbody>
                {isLoading ? (
                  Array.from({ length: 7 }).map((_, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f5f5f5' }}>
                      <Td>
                        <SkeletonBlock $h="0.9rem" $w="75%" />
                      </Td>
                      <Td>
                        <SkeletonBlock $h="1.8rem" />
                      </Td>
                      <Td>
                        <SkeletonBlock $h="0.9rem" $w="65%" />
                      </Td>
                      <Td>
                        <SkeletonBlock $h="0.9rem" $w="85%" />
                      </Td>
                      <Td>
                        <SkeletonBlock $h="1.8rem" />
                      </Td>
                      <Td style={{ textAlign: 'center' }}>
                        <SkeletonBlock $h="0.9rem" $w="60%" />
                      </Td>
                      <Td style={{ textAlign: 'center' }}>
                        <SkeletonBlock $h="0.9rem" $w="50%" />
                      </Td>
                    </tr>
                  ))
                ) : rounds.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      style={{
                        textAlign: 'center',
                        padding: '3rem 1rem',
                        color: '#9ca3af',
                        fontFamily: 'RedHatDisplay-Regular',
                        fontSize: textSize(0.9),
                      }}
                    >
                      Nenhuma rodada cadastrada ainda.
                    </td>
                  </tr>
                ) : (
                  modalityGroups.map(group => (
                    <React.Fragment key={group.slug}>
                      <ModalityGroupRow>
                        <ModalityGroupCell colSpan={7}>
                          {group.label}
                        </ModalityGroupCell>
                      </ModalityGroupRow>

                      {group.display.map(row => {
                        if (row.isBreak) {
                          return (
                            <BreakRow key={`${group.slug}-${row.id}`}>
                              <BreakCell colSpan={7}>
                                {row.breakLabel} · {formatTime(row.starts_at)} –{' '}
                                {formatTime(row.ends_at)}
                              </BreakCell>
                            </BreakRow>
                          );
                        }

                        const teamIndex = group.teamIndexById.get(row.id) ?? 0;

                        return (
                          <TableRow key={row.id} $animDir={row.animDir}>
                            <Td title={row.team}>{row.team}</Td>
                            <Td>
                              <ArenaSelect
                                value={row.arenaId}
                                onChange={e =>
                                  handleArenaChange(row.id, e.target.value)
                                }
                              >
                                <option value="">Selecione...</option>
                                {arenaOptions
                                  .filter(
                                    a =>
                                      !row.modalitySlug ||
                                      a.modality === row.modalitySlug,
                                  )
                                  .map(a => (
                                    <option key={a.id} value={a.id}>
                                      {a.name}
                                    </option>
                                  ))}
                              </ArenaSelect>
                            </Td>
                            <Td title={row.modality}>{row.modality}</Td>
                            <Td title={row.category}>{row.category}</Td>
                            <Td>
                              <ArenaSelect
                                value={row.judgeId ?? ''}
                                onChange={e =>
                                  handleJudgeChange(row.id, e.target.value)
                                }
                              >
                                <option value="">Sem juiz</option>
                                {judgeOptions.map(j => (
                                  <option key={j.id} value={j.id}>
                                    {j.name}
                                  </option>
                                ))}
                              </ArenaSelect>
                            </Td>
                            <TdTime>{formatTime(row.starts_at)}</TdTime>
                            <Td>
                              <OrdemCell>
                                <OrdemBtn
                                  type="button"
                                  aria-label={`Mover ${row.team} para cima`}
                                  $visible={teamIndex > 0}
                                  onClick={() =>
                                    moveRow(group.teams, teamIndex, 'up')
                                  }
                                >
                                  <ChevronUpIcon
                                    width="0.7rem"
                                    stroke="currentColor"
                                    fill="none"
                                    strokeWidth={2.5}
                                  />
                                </OrdemBtn>
                                <OrdemNumber>{teamIndex + 1}</OrdemNumber>
                                <OrdemBtn
                                  type="button"
                                  aria-label={`Mover ${row.team} para baixo`}
                                  $visible={teamIndex < group.teams.length - 1}
                                  onClick={() =>
                                    moveRow(group.teams, teamIndex, 'down')
                                  }
                                >
                                  <ChevronDownIcon
                                    width="0.7rem"
                                    stroke="currentColor"
                                    fill="none"
                                    strokeWidth={2.5}
                                  />
                                </OrdemBtn>
                              </OrdemCell>
                            </Td>
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </ScheduleTable>
          </TableScroller>
        </TableWrapper>
      )}
    </PageWrapper>
  );
};

export default TournamentTimeline;
